/* eslint-disable jsx-a11y/alt-text */
import React, { useState } from "react"
import { useTranslation, Trans } from "react-i18next"
import { Box, Button, Dialog, DialogActions, DialogContent, MobileStepper, Typography } from "@mui/material"
import AttireIcon from "../../icon/headshot-tips-attire.svg"
import AccessoriesIcon from "../../icon/headshot-tips-accessories.svg"
import { ReactComponent as LightIcon } from "../../icon/headshot-tips-light.svg"
import { ReactComponent as AngleIcon } from "../../icon/headshot-tips-angle.svg"
import { ReactComponent as FrameIcon } from "../../icon/headshot-tips-frame.svg"
import { ReactComponent as BackBracket } from "../../icon/back-bracket.svg"
import BobbingIcon from "../common/BobbingIcon"
import BlueButton from "../buttons/BlueButton"

interface HeadshotTipsModalProps {
  show: boolean
  onHide?: () => void
}

const slides = [
  {
    icon: (children: React.ReactNode) => (
      <BobbingIcon horizontalOffset="-73px" verticalOffset="-75px">
        <img src={AttireIcon} />
      </BobbingIcon>
    ),
    titleKey: "headshotTips.appearance.title",
    content: (t: any) => (
      <Typography sx={{ fontSize: 18, fontWeight: 400, mt: 1, minHeight: 66 }}>
        <Trans i18nKey="headshotTips.appearance.text"><b></b></Trans>
      </Typography>
    ),
  },
  {
    icon: (children: React.ReactNode) => (
      <BobbingIcon horizontalOffset="-73px" verticalOffset="-75px">
        <img src={AccessoriesIcon} />
      </BobbingIcon>
    ),
    titleKey: "headshotTips.accessories.title",
    content: (t: any) => (
      <Box component="ul" sx={{ listStyleType: "none", pl: 0, fontSize: 19, lineHeight: "33px", fontWeight: 300, color: "#323232" }}>
        <li><Trans i18nKey="headshotTips.accessories.statements.line1"><b></b></Trans></li>
        <li><Trans i18nKey="headshotTips.accessories.statements.line2"><b></b></Trans></li>
      </Box>
    ),
  },
  {
    icon: () => (
      <BobbingIcon horizontalOffset="-82px" verticalOffset="-90px">
        <LightIcon />
      </BobbingIcon>
    ),
    titleKey: "headshotTips.light.title",
    contentKey: "headshotTips.light.text",
  },
  {
    icon: () => (
      <BobbingIcon horizontalOffset="-87px" verticalOffset="-70px">
        <AngleIcon />
      </BobbingIcon>
    ),
    titleKey: "headshotTips.angle.title",
    contentKey: "headshotTips.angle.text",
  },
  {
    icon: () => (
      <BobbingIcon horizontalOffset="-83px" verticalOffset="-100px">
        <FrameIcon />
      </BobbingIcon>
    ),
    titleKey: "headshotTips.frame.title",
    contentKey: "headshotTips.frame.text",
  },
]

function HeadshotTipsModal({ show, onHide }: HeadshotTipsModalProps) {
  const [activeIndex, setActiveIndex] = useState<number>(0)
  const { t } = useTranslation()
  const slide = slides[activeIndex]

  return (
    <Dialog
      open={show}
      onClose={onHide}
      disableEscapeKeyDown
      slotProps={{ paper: { sx: { borderRadius: "0.7rem", color: "#323232", maxWidth: 480 } } }}
    >
      <DialogContent sx={{ textAlign: "center", pb: 0 }}>
        {slide.icon(null)}
        <Typography fontSize={28} fontWeight={500}>{t(slide.titleKey)}</Typography>
        {slide.content ? (
          slide.content(t)
        ) : (
          <Typography sx={{ fontSize: 18, fontWeight: 400, mt: 1, minHeight: 66 }}>
            {t(slide.contentKey!)}
          </Typography>
        )}
        <MobileStepper
          variant="dots"
          steps={slides.length}
          position="static"
          activeStep={activeIndex}
          sx={{
            justifyContent: "center",
            background: "transparent",
            "& .MuiMobileStepper-dot": { bgcolor: "#ccc" },
            "& .MuiMobileStepper-dotActive": { bgcolor: "#30aade" },
          }}
          backButton={null}
          nextButton={null}
        />
      </DialogContent>
      <DialogActions sx={{ justifyContent: "center", px: 3, pb: 2 }}>
        {activeIndex > 0 && (
          <Button
            onClick={() => setActiveIndex(activeIndex - 1)}
            sx={{ color: "#30aade", textTransform: "none", mr: "auto" }}
          >
            <BackBracket style={{ marginBottom: "3px", marginRight: 4 }} /> {t("headshotTips.back")}
          </Button>
        )}
        {activeIndex < slides.length - 1 ? (
          <BlueButton
            onClick={() => setActiveIndex(activeIndex + 1)}
            sx={{ px: { xs: "40px", sm: "60px" }, ml: "auto" }}
          >
            {t("headshotTips.next")}
          </BlueButton>
        ) : (
          <BlueButton
            onClick={onHide}
            sx={{ px: { xs: "40px", sm: "60px" }, ml: "auto" }}
          >
            {t("headshotTips.close")}
          </BlueButton>
        )}
      </DialogActions>
    </Dialog>
  )
}

export default HeadshotTipsModal
